import { Box, IconButton } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { GetDashboardDataResponse } from "../../../api/dashboard";
import { numberWithCommas } from "../../../helpers/numberWithCommas";
import { FONT_DISPLAY } from "../../theme/v2Tokens";
import KpiCard from "./KpiCard";

/* ── Chevron icons for tag carousel ───────────────────────────────── */

const ChevronLeft = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M10 4l-4 4 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ── Component ─────────────────────────────────────────────────────── */

interface KpiStripProps {
  data: GetDashboardDataResponse;
}

export default function KpiStrip({ data }: KpiStripProps) {
  const { t } = useTranslation("dashboards");
  const [tagIndex, setTagIndex] = useState(0);
  const tags = data.interactionsByTags ?? [];
  const tagCount = tags.length;

  const prevTag = () => setTagIndex((i) => (i - 1 + tagCount) % tagCount);
  const nextTag = () => setTagIndex((i) => (i + 1) % tagCount);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: "12px",
        padding: "0 24px 16px",
        flexShrink: 0,
      }}
    >
      {/* Card 1: Legal Hold */}
      <KpiCard title={t("legalHolds")} accentColor="var(--chart-4)">
        {numberWithCommas(data.countLegalHold) ?? "0"}
      </KpiCard>

      {/* Card 2: Purging */}
      <KpiCard title={t("purging30Days")} accentColor="var(--chart-5)">
        {numberWithCommas(data.countInteractionsExpiring) ?? "0"}
      </KpiCard>

      {/* Card 3: Total Interactions */}
      <KpiCard title={t("totalInteractions")} accentColor="var(--chart-1)">
        <Box sx={{ display: "flex", alignItems: "baseline", gap: "12px", flexWrap: "wrap" }}>
          <span>{numberWithCommas(data.interactionTotal) ?? "0"}</span>
          <Box
            component="span"
            sx={{
              fontSize: "0.75rem",
              fontWeight: 400,
              fontFamily: FONT_DISPLAY,
              color: "var(--text-tertiary)",
            }}
          >
            {t("noMedia")} {numberWithCommas(data.interactionTotalWithoutMedia) ?? "0"}
          </Box>
        </Box>
      </KpiCard>

      {/* Card 4: Tags carousel */}
      <KpiCard title={tagCount > 0 ? `${t("tags")}: ${tags[tagIndex].name}` : t("tags")} accentColor="var(--chart-2)">
        {tagCount > 0 ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton
              onClick={prevTag}
              size="small"
              aria-label={t("previousTag")}
              sx={{
                color: "var(--text-tertiary)",
                padding: "2px",
                "&:hover": { bgcolor: "var(--bg-hover)" },
              }}
            >
              <ChevronLeft />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  fontFamily: FONT_DISPLAY,
                  fontSize: "1.6rem",
                  fontWeight: 500,
                  color: "var(--chart-2)",
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                }}
              >
                {numberWithCommas(tags[tagIndex].value) ?? "0"}
              </Box>
            </Box>
            <IconButton
              onClick={nextTag}
              size="small"
              aria-label={t("nextTag")}
              sx={{
                color: "var(--text-tertiary)",
                padding: "2px",
                "&:hover": { bgcolor: "var(--bg-hover)" },
              }}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        ) : (
          <Box sx={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>{t("noTags")}</Box>
        )}
      </KpiCard>
    </Box>
  );
}
